import { useEffect, useRef, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';

import { useReportError } from '@/hooks/useReportError';

import type { GraphLayoutInput } from '../householdLoadingGraph.worker';
import type { GraphComposition } from '../householdLoadingGraphLayout';

export type GraphLayout = GraphLayoutInput & { composition: GraphComposition };

export function useHouseholdLoadingGraphLayout(input: GraphLayoutInput) {
  const [layout, setLayout] = useState<GraphLayout>();
  const [failed, setFailed] = useState(false);
  const submit = useRef<((input: GraphLayoutInput) => void) | undefined>(
    undefined
  );
  const { reportError } = useReportError();

  useEffect(() => {
    let worker: Worker | undefined;
    let stopped = false;
    let running: GraphLayoutInput | undefined;
    let pending: GraphLayoutInput | undefined;
    const fail = (error: Error) => {
      stopped = true;
      worker?.terminate();
      setFailed(true);
      reportError('Failed to lay out household loading preview', error);
    };
    try {
      // Dense previews need hundreds of force-layout ticks and label collision
      // checks per snapshot. On the main thread, this blocks animation and input.
      // An effect or timer would still block that thread; use a worker instead.
      worker = new Worker(
        new URL('../householdLoadingGraph.worker.ts', import.meta.url),
        { type: 'module' }
      );
      const send = (next: GraphLayoutInput) => {
        running = next;
        try {
          worker?.postMessage(next);
        } catch (error) {
          fail(error instanceof Error ? error : new Error(String(error)));
        }
      };
      submit.current = (next) => {
        if (stopped) return;
        // Extraction can publish faster than layout completes. Keep only the
        // newest waiting snapshot so the graph does not fall behind the stream.
        if (running) pending = next;
        else send(next);
      };
      worker.onmessage = ({ data }: MessageEvent<GraphComposition>) => {
        if (stopped || !running) return;
        // Publish the matching data and layout together. New props must not
        // supply names or edges for an older layout that is still on screen.
        setLayout({ ...running, composition: data });
        running = undefined;
        if (pending) {
          const next = pending;
          pending = undefined;
          send(next);
        }
      };
      worker.onerror = (event) => fail(new Error(event.message));
      worker.onmessageerror = () =>
        fail(new Error('Invalid graph worker reply'));
    } catch (error) {
      fail(error instanceof Error ? error : new Error(String(error)));
    }
    return () => {
      stopped = true;
      submit.current = undefined;
      worker?.terminate();
    };
  }, [reportError]);

  // Query updates also contain unchanged previews. Do not settle those again.
  useDeepCompareEffect(() => {
    submit.current?.(input);
  }, [input, reportError]);

  return { layout, failed };
}
